const DEFAULT_CHANNEL_NAME = "ouro-tab-sync";
const STORAGE_KEY_PREFIX = "__tabSyncChannel__:";
const MAX_DEDUPE_IDS = 200;

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

let fallbackSeq = 0;

function makeSyncId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch {
      /* fall through */
    }
  }
  fallbackSeq += 1;
  return `${Date.now()}-${fallbackSeq}-${Math.random().toString(36).slice(2)}`;
}

/**
 * @param {unknown} raw
 * @returns {{ id: string | null, body: unknown }}
 */
function unwrapTransportPayload(raw) {
  if (
    raw &&
    typeof raw === "object" &&
    typeof raw.__tabSyncId === "string" &&
    Object.prototype.hasOwnProperty.call(raw, "__tabSyncBody")
  ) {
    return { id: raw.__tabSyncId, body: raw.__tabSyncBody };
  }
  return { id: null, body: raw };
}

/**
 * Cross-tab fan-out: BroadcastChannel when available, plus localStorage storage events
 * for other tabs, plus in-tab listeners. Tolerant of missing APIs and quota errors.
 * Duplicate deliveries of the same transport id (BC + storage) are deduped.
 */
export function createTabSyncChannel({
  channelName = DEFAULT_CHANNEL_NAME,
  windowRef = typeof window !== "undefined" ? window : null,
  storage =
    typeof localStorage !== "undefined" ? localStorage : null,
  broadcastChannelFactory,
} = {}) {
  const localListeners = new Set();
  let closed = false;
  let bc = null;
  /** @type {'addEventListener' | 'onmessage' | null} */
  let bcMessageBinding = null;
  let unsubStorage = null;
  const seenSyncIds = new Set();
  const syncIdQueue = [];

  const tryOpenBroadcastChannel = () => {
    if (broadcastChannelFactory) {
      try {
        return broadcastChannelFactory(channelName);
      } catch {
        return null;
      }
    }
    if (typeof BroadcastChannel !== "undefined") {
      try {
        return new BroadcastChannel(channelName);
      } catch {
        return null;
      }
    }
    return null;
  };

  bc = tryOpenBroadcastChannel();

  const dispatchLocal = (message) => {
    if (closed) return;
    for (const fn of localListeners) {
      try {
        fn(message);
      } catch {
        /* ignore listener errors */
      }
    }
  };

  function ingestTransportPayload(wireOrRaw) {
    if (closed) return;
    const { id, body } = unwrapTransportPayload(wireOrRaw);
    if (id == null) {
      dispatchLocal(body);
      return;
    }
    if (seenSyncIds.has(id)) return;
    seenSyncIds.add(id);
    syncIdQueue.push(id);
    while (syncIdQueue.length > MAX_DEDUPE_IDS) {
      const old = syncIdQueue.shift();
      seenSyncIds.delete(old);
    }
    dispatchLocal(body);
  }

  const onBcMessage = (ev) => {
    if (closed || !ev) return;
    ingestTransportPayload(ev.data);
  };

  if (bc) {
    try {
      if (typeof bc.addEventListener === "function") {
        bc.addEventListener("message", onBcMessage);
        bcMessageBinding = "addEventListener";
      } else if (typeof bc.onmessage !== "undefined") {
        bc.onmessage = onBcMessage;
        bcMessageBinding = "onmessage";
      }
    } catch {
      /* ignore */
    }
  }

  const storageKey = `${STORAGE_KEY_PREFIX}${channelName}`;

  const onStorage = (e) => {
    if (closed || !e || e.key !== storageKey || e.newValue == null) return;
    const parsed = safeJsonParse(e.newValue);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !Object.prototype.hasOwnProperty.call(parsed, "payload")
    ) {
      return;
    }
    ingestTransportPayload(parsed.payload);
  };

  if (windowRef && typeof windowRef.addEventListener === "function") {
    try {
      windowRef.addEventListener("storage", onStorage);
      unsubStorage = () => {
        try {
          windowRef.removeEventListener("storage", onStorage);
        } catch {
          /* ignore */
        }
      };
    } catch {
      unsubStorage = null;
    }
  }

  function post(message) {
    if (closed) return;

    const id = makeSyncId();
    const wire = { __tabSyncId: id, __tabSyncBody: message };

    if (bc) {
      try {
        if (typeof bc.postMessage === "function") {
          bc.postMessage(wire);
        }
      } catch {
        /* private mode / closed port */
      }
    }

    if (storage) {
      try {
        const envelope = JSON.stringify({
          payload: wire,
          t: Date.now(),
        });
        storage.setItem(storageKey, envelope);
      } catch {
        /* quota / private mode */
      }
    }

    ingestTransportPayload(wire);
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      return () => {};
    }
    localListeners.add(listener);
    return () => {
      localListeners.delete(listener);
    };
  }

  function close() {
    if (closed) return;
    closed = true;
    localListeners.clear();
    seenSyncIds.clear();
    syncIdQueue.length = 0;
    if (unsubStorage) {
      unsubStorage();
      unsubStorage = null;
    }
    if (bc) {
      try {
        if (
          bcMessageBinding === "addEventListener" &&
          typeof bc.removeEventListener === "function"
        ) {
          bc.removeEventListener("message", onBcMessage);
        } else if (bcMessageBinding === "onmessage") {
          bc.onmessage = null;
        }
      } catch {
        /* ignore */
      }
      bcMessageBinding = null;
      try {
        if (typeof bc.close === "function") {
          bc.close();
        }
      } catch {
        /* ignore */
      }
      bc = null;
    }
  }

  return { post, subscribe, close };
}
